import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AdmytService } from '../../../services/admyt.service'
import { Location } from '@angular/common'
import { Firestore, collection, query, where, getDocs } from '@angular/fire/firestore'
import { FirestoreStructure } from '@parkupp/core'

interface AdmytUser {
    id: string
    firstName: string
    lastName: string
    email: string
    status?: string
    createdAt?: string
    updatedAt?: string
    cars?: any[]
    tenants?: any[]
    isActive?: boolean
}

interface AdmytUserCar {
    id: string
    plate: string
    make: string
    model: string
    createdAt?: string
}

interface LinkedParkUppClient {
    uid: string
    firstName: string
    lastName: string
}

@Component({
    selector: 'app-admin-view-admyt-user',
    templateUrl: './admin-view-admyt-user.component.html',
    styleUrls: ['./admin-view-admyt-user.component.scss'],
})
export class AdminViewAdmytUserComponent implements OnInit {
    userId: string
    user: AdmytUser | null = null
    userCars: AdmytUserCar[] = []
    loading = true
    error: string | null = null
    carsLoading = false
    
    // Linked ParkUpp client
    linkedClient: LinkedParkUppClient | null = null

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private admytService: AdmytService,
        private firestore: Firestore
    ) {}

    async ngOnInit() {
        this.userId = this.route.snapshot.params['userId']
        await this.loadUserDetails()
        await this.loadUserCars()
        await this.loadLinkedParkUppClient()
    }

    async loadUserDetails() {
        try {
            this.loading = true
            this.error = null

            // Get user details from the users list
            const usersResult = await this.admytService.getUsers()
            const usersResultData = usersResult.data as any
            console.log('Users result:', usersResult)

            const users = (usersResultData.data as AdmytUser[]) || []
            this.user = users.find((u) => u.id === this.userId) || null

            if (!this.user) {
                this.error = `User with ID "${this.userId}" not found.`
            }

            this.loading = false
        } catch (error) {
            console.error('Failed to load user details:', error)
            this.error = 'Failed to load user details. Please try again.'
            this.loading = false
        }
    }

    async loadUserCars() {
        try {
            this.carsLoading = true

            // Get user cars
            const carsResult = await this.admytService.getUserCars(this.userId)
            console.log('User cars result:', carsResult)
            this.userCars = (carsResult.data as any) || []

            this.carsLoading = false
        } catch (error) {
            console.error('Failed to load user cars:', error)
            this.carsLoading = false
        }
    }

    goBack() {
        this.location.back()
    }

    goToUsersList() {
        this.router.navigate(['/admin/admyt-users'])
    }

    getFullName(): string {
        if (!this.user) return 'Unknown User'
        return `${this.user.firstName || ''} ${this.user.lastName || ''}`.trim() || 'Unknown User'
    }

    async refreshData() {
        await this.loadUserDetails()
        await this.loadUserCars()
        await this.loadLinkedParkUppClient()
    }

    async loadLinkedParkUppClient() {
        if (!this.user?.email) return
        
        try {
            const adminClientsRef = collection(this.firestore, FirestoreStructure.ADMIN_CLIENTS)
            const q = query(adminClientsRef, where('email', '==', this.user.email))
            const snapshot = await getDocs(q)

            if (!snapshot.empty) {
                const doc = snapshot.docs[0]
                
                // Get the client's name from the clients collection
                const clientsRef = collection(this.firestore, FirestoreStructure.CLIENTS)
                const clientDoc = await getDocs(query(clientsRef, where('__name__', '==', doc.id)))

                let firstName = ''
                let lastName = ''

                if (!clientDoc.empty) {
                    const clientData = clientDoc.docs[0].data()
                    firstName = clientData['firstName'] || ''
                    lastName = clientData['lastName'] || ''
                }

                this.linkedClient = {
                    uid: doc.id,
                    firstName,
                    lastName,
                }
            }
        } catch (error) {
            console.error('Failed to load linked ParkUpp client:', error)
        }
    }

    /**
     * Parse subscription ID from tenant name
     */
    getSubscriptionFromTenantName(name: string): string | null {
        if (!name) return null
        const parts = name.split(' ')
        if (parts.length >= 3) {
            return parts[parts.length - 1]
        }
        return null
    }

    /**
     * Get status class for styling
     */
    getStatusClass(status: string | boolean | undefined): string {
        if (typeof status === 'boolean') {
            return status ? 'admyt-status admyt-status--active' : 'admyt-status admyt-status--inactive'
        }
        switch (status?.toLowerCase()) {
            case 'active':
                return 'admyt-status admyt-status--active'
            case 'inactive':
                return 'admyt-status admyt-status--inactive'
            case 'pending':
                return 'admyt-status admyt-status--pending'
            default:
                return 'admyt-status admyt-status--inactive'
        }
    }

    async deleteUserCar(carPlate: string) {
        if (!confirm(`Are you sure you want to delete the car with plate "${carPlate}"?`)) {
            return
        }

        try {
            await this.admytService.deleteUserCar(this.userId, carPlate)
            await this.loadUserCars() // Refresh the cars list
        } catch (error: any) {
            console.error('Failed to delete car:', error)

            // Extract specific error message from response if available
            let errorMessage = 'Failed to delete car. Please try again.'
            if (error?.responseData?.error) {
                errorMessage = error.responseData.error
            } else if (error?.message) {
                errorMessage = error.message
            }

            alert(errorMessage)
        }
    }

    /**
     * Truncate any value (string or number) to specified length
     */
    truncate(value: any, maxLength: number): string {
        if (value === null || value === undefined) return ''
        const str = String(value)
        if (str.length <= maxLength) return str
        return str.substring(0, maxLength) + '...'
    }
}
